import React, { useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import {
  Badge,
  Button,
  Spinner,
  Table,
} from "reactstrap";
import * as XLSX from "xlsx";
import cogoToast from "cogo-toast";
import { FaArrowLeft, FaDownload, FaFileExcel, FaUpload } from "react-icons/fa";
import { createWashProjectApi } from "../../../services/Api";
import { SUCCESS_RESULT } from "../../../Constants";
import {
  WASH_EXCEL_COLUMNS,
  canCreateWashProject,
  validateWashExcelRows,
} from "./washProjectHelpers";
import "./WashProjects.css";

const WashProjectExcelUpload = () => {
  const history = useHistory();
  const fileInputRef = useRef(null);
  const canCreate = canCreateWashProject();

  const [fileName, setFileName] = useState("");
  const [validProjects, setValidProjects] = useState([]);
  const [invalidRows, setInvalidRows] = useState([]);
  const [parseError, setParseError] = useState("");
  const [unknownHeaders, setUnknownHeaders] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [uploading, setUploading] = useState(false);

  if (!canCreate) {
    return (
      <div className="container-xl mt-4 wash-page">
        <div className="wash-card">
          <div className="wash-card__body text-center text-muted py-5">
            You do not have permission to upload WASH projects.
            <div className="mt-3">
              <Button color="link" tag={Link} to="/projects/wash">
                Back to list
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const resetState = () => {
    setFileName("");
    setValidProjects([]);
    setInvalidRows([]);
    setParseError("");
    setUnknownHeaders([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([WASH_EXCEL_COLUMNS]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "WASH Projects");
    XLSX.writeFile(wb, "wash_projects_upload_template.xlsx");
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    setValidProjects([]);
    setInvalidRows([]);
    setParseError("");
    setUnknownHeaders([]);
    if (!file) {
      setFileName("");
      return;
    }

    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      setParseError("Please upload an Excel file (.xlsx or .xls).");
      setFileName(file.name);
      return;
    }

    setFileName(file.name);
    setParsing(true);
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const rowsAoA = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        defval: "",
      });

      if (!rowsAoA.length) {
        setParseError("Uploaded file is empty.");
        return;
      }

      const result = validateWashExcelRows(rowsAoA);
      setUnknownHeaders(result.unknownHeaders || []);

      if (!result.ok) {
        setParseError(result.error || "Could not parse the Excel file.");
        return;
      }

      setValidProjects(result.validProjects);
      setInvalidRows(result.invalidRows);

      if (!result.validProjects.length && !result.invalidRows.length) {
        setParseError("No data rows found in the Excel file.");
        return;
      }

      if (result.invalidRows.length) {
        cogoToast.warn(
          `${result.invalidRows.length} row(s) have errors. Fix them before uploading.`,
          { hideAfter: 5 }
        );
      } else {
        cogoToast.success(
          `${result.validProjects.length} valid project(s) ready to upload.`,
          { hideAfter: 3 }
        );
      }
    } catch (err) {
      setParseError(err?.message || "Failed to read the Excel file.");
    } finally {
      setParsing(false);
    }
  };

  const handleUpload = () => {
    if (invalidRows.length) {
      cogoToast.error("Fix all validation errors before uploading.", {
        hideAfter: 4,
      });
      return;
    }
    if (!validProjects.length) {
      cogoToast.error("No valid projects to upload.", { hideAfter: 4 });
      return;
    }

    setUploading(true);
    createWashProjectApi({ projects: validProjects })
      .then((res) => {
        setUploading(false);
        if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
          cogoToast.success(
            res.data.message ||
              `${validProjects.length} WASH project(s) created successfully.`,
            { hideAfter: 4 }
          );
          history.push("/projects/wash");
        } else {
          cogoToast.error(res.data?.message || "Upload failed", {
            hideAfter: 5,
          });
        }
      })
      .catch((err) => {
        setUploading(false);
        cogoToast.error(
          err?.response?.data?.message ||
            "Something went wrong uploading the projects.",
          { hideAfter: 5 }
        );
      });
  };

  const canSubmit =
    validProjects.length > 0 && invalidRows.length === 0 && !uploading && !parsing;

  return (
    <div className="container-xl mt-4 mb-5 wash-page">
      <div className="wash-page__header">
        <div>
          <Button color="link" className="px-0 mb-2" tag={Link} to="/projects/wash">
            <FaArrowLeft className="me-1" /> Back to Wash Projects
          </Button>
          <h1 className="wash-page__title">Upload From Excel</h1>
          <p className="wash-page__subtitle">
            Upload a spreadsheet of WASH projects. Rows are validated before
            bulk create.
          </p>
        </div>
        <Button color="secondary" outline onClick={downloadTemplate}>
          <FaDownload className="me-1" /> Download template
        </Button>
      </div>

      <div className="wash-card mb-3">
        <div className="wash-card__body">
          <div className="wash-form-section mb-0">
            <h2 className="wash-form-section__title">
              <FaFileExcel className="me-2" />
              Select Excel file
            </h2>
            <p className="wash-form-section__desc">
              Expected columns: S/N, project, year_established, water_source,
              work_type, details, depth, yield_or_volume, water_quality, donor,
              region, district, site_name, latitude, longitude, program_area,
              functionality, budget_value, beneficiary_type, Response Type,
              total_beneficiaries, remarks.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              className="form-control"
              onChange={handleFileChange}
              disabled={parsing || uploading}
            />
            {fileName ? (
              <div className="text-muted small mt-2">Selected: {fileName}</div>
            ) : null}
            {parsing ? (
              <div className="mt-3">
                <Spinner size="sm" className="me-2" /> Reading file…
              </div>
            ) : null}
            {parseError ? (
              <div className="alert alert-danger mt-3 mb-0">{parseError}</div>
            ) : null}
            {unknownHeaders.length ? (
              <div className="alert alert-warning mt-3 mb-0">
                Ignored unknown column(s): {unknownHeaders.join(", ")}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {(validProjects.length > 0 || invalidRows.length > 0) && (
        <div className="wash-card mb-3">
          <div className="wash-card__body">
            <div className="d-flex gap-2 flex-wrap align-items-center mb-3">
              <Badge color="success" pill>
                {validProjects.length} valid
              </Badge>
              <Badge color={invalidRows.length ? "danger" : "secondary"} pill>
                {invalidRows.length} with errors
              </Badge>
            </div>

            {invalidRows.length > 0 && (
              <>
                <h5 className="text-danger mb-2">Rows with errors</h5>
                <p className="text-muted small">
                  Fix these in Excel and re-upload. Upload is blocked while any
                  errors remain.
                </p>
                <div className="table-responsive mb-4">
                  <Table bordered size="sm" className="wash-table mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: 80 }}>Excel row</th>
                        <th>Project</th>
                        <th>Errors</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invalidRows.map((item) => (
                        <tr key={`err-${item.row}`}>
                          <td>{item.row}</td>
                          <td>{item.project}</td>
                          <td>
                            <ul className="mb-0 ps-3">
                              {item.errors.map((err) => (
                                <li key={`${item.row}-${err}`}>{err}</li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </>
            )}

            {validProjects.length > 0 && (
              <>
                <h5 className="mb-2">Valid projects preview</h5>
                <div className="table-responsive">
                  <Table bordered size="sm" className="wash-table mb-0">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Project</th>
                        <th>Year</th>
                        <th>Region</th>
                        <th>District</th>
                        <th>Site</th>
                        <th>Water source</th>
                        <th>Work type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validProjects.slice(0, 50).map((p, idx) => (
                        <tr key={`ok-${idx}-${p.project}`}>
                          <td>{idx + 1}</td>
                          <td>{p.project}</td>
                          <td>{p.year_established}</td>
                          <td>{p.region}</td>
                          <td>{p.district}</td>
                          <td>{p.site_name}</td>
                          <td>{p.water_source}</td>
                          <td>{p.work_type}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                {validProjects.length > 50 ? (
                  <div className="text-muted small mt-2">
                    Showing first 50 of {validProjects.length} valid rows.
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      )}

      <div className="wash-footer">
        <div className="d-flex gap-2">
          <Button color="secondary" outline tag={Link} to="/projects/wash" disabled={uploading}>
            Cancel
          </Button>
          <Button color="secondary" outline onClick={resetState} disabled={uploading || parsing}>
            Clear file
          </Button>
        </div>
        <Button color="primary" onClick={handleUpload} disabled={!canSubmit}>
          {uploading ? (
            <>
              <Spinner size="sm" className="me-2" /> Uploading…
            </>
          ) : (
            <>
              <FaUpload className="me-1" /> Upload {validProjects.length || ""}{" "}
              project{validProjects.length === 1 ? "" : "s"}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default WashProjectExcelUpload;
